// rateizzazioni-api.js — amm-v3 (A5, ondata 32): parte PURA della vista "Piani
// di rientro" (pagina 'rateizzazioni' — lista dei piani di rateazione),
// testabile sotto node (contract test server/src/lib/amm-v3-rateizzazioni.test.js).
//
// PRINCIPI (rito A1..A4/proc-v3, spike o27 §2.2/§2.3):
//  - TESTI VERBATIM dal v1 (admin-module.js, renderRateizzazioni r.7396-7433):
//    il contract test pinna OGNI stringa di RZ_TEXTS sul sorgente v1.
//  - FORMULE del v1 replicate come funzioni pure (KPI residuo/attivi, progresso
//    rate, data prossima): stessa semantica, stessi quirk (residuo null ⇒ 0).
//  - DELEGHE alle primitive v1 (mai reimplementate): editor "+ Nuovo piano"
//    (AMM.editRateizzazione), dettaglio + RATE EDITABILI (AMM.openRateizzazione:
//    il modale con openRataEdit/rate.update, dove vivono le invarianti CAS/
//    doppio-salva/no-perdita-dati — restano V1), elimina (AMM.delSafe), badge
//    stato (AMM.pianoStatoBadge, HTML iniettato), fmtEur2, isAdmin, toast.
//    admin-module.js resta OFF-LIMITS: zero byte cambiati lì.
//  - ZERO bypass di render: editRateizzazione riceve l'onSaved che GLI PASSA il
//    v3 (refresh v3 via renderPage), openRateizzazione si auto-gestisce il
//    reload interno (mai renderRateizzazioni diretto) ⇒ NIENTE interceptor.
//  - MAI 'nav:badge' o canali nuovi da client/src/amm-v3/**.

import { apiPathFn } from './api.js';

export const RZ_DELEGATED_METHODS = [
  'renderPage',           // il seam stesso (wrap) + refresh post-azione
  'renderRateizzazioni',  // la vista v1 wrappata (drift guard: rename ⇒ rosso)
  'toast',                // esito eliminazione (stesso canale del v1)
  'isAdmin',              // bottone "elimina" solo admin (parità r.7419)
  'delSafe',              // eliminazione con alert d'errore (primitiva v1 r.878)
  'fmtEur2',              // formattazione € (unica fonte: v1)
  'pianoStatoBadge',      // badge stato piano (HTML v1, iniettato verbatim)
  'editRateizzazione',    // modale "+ Nuovo piano" — resta v1
  'openRateizzazione',    // modale dettaglio + rate editabili — resta v1
];

export const RZ_API_PATHS = [
  'rateizzazioni.list',   // GET /rateizzazioni
  'rateizzazioni.remove', // DELETE /rateizzazioni/:id
];

// ---- TESTI VERBATIM dal v1 (pinnati dal contract test) ----
export const RZ_TEXTS = {
  title: 'Piani di rientro',
  titleTooltip: "Tecnicamente 'Rateizzazioni'",
  nuovoPiano: '+ Nuovo piano',
  kpiAttivi: 'Piani attivi',
  kpiResiduo: 'Debito residuo',
  kpiTotali: 'Piani totali',
  colOggetto: 'Oggetto',
  colEnte: 'Ente',
  colTotale: 'Totale',
  colRate: 'Rate',
  colResiduo: 'Residuo',
  colProssima: 'Prossima',
  colStato: 'Stato',
  decaduta: 'decaduta',
  elimina: 'elimina',
  empty: 'Nessun piano. Creane uno con "+ Nuovo piano" (anche da un atto rateizzabile).',
  confirmElimina: 'Eliminare il piano e le sue rate-scadenze?',
  toastEliminato: 'Piano eliminato',
  dash: '—',
};

// data breve italiana (r.7399): 'gg/mm/aaaa' o '—' (dd del v1)
export function formatData(s) {
  return s ? new Date(s + 'T00:00:00').toLocaleDateString('it-IT') : RZ_TEXTS.dash;
}

// solo i piani attivi (v1 r.7400/7405): filtro condiviso da KPI e conteggi
export function pianiAttivi(list) {
  return (list || []).filter((p) => p.stato === 'attivo');
}

// debito residuo totale — PARITÀ v1 r.7400: somma dei residui SOLO dei piani
// attivi; riepilogo assente ⇒ 0 (mai NaN).
export function residuoTotale(list) {
  return pianiAttivi(list).reduce((t, p) => t + (p.riepilogo ? p.riepilogo.residuo : 0), 0);
}

// KPI del riquadro (v1 r.7405): attivi, debito residuo €, totali.
export function kpiDefs(list, fmtEur2) {
  return [
    { label: RZ_TEXTS.kpiAttivi, val: String(pianiAttivi(list).length) },
    { label: RZ_TEXTS.kpiResiduo, val: fmtEur2(residuoTotale(list)) },
    { label: RZ_TEXTS.kpiTotali, val: String((list || []).length) },
  ];
}

// progresso rate della riga (v1 r.7415): 'n_pagate/n_rate' (fallback p.n_rate)
export function rateProgress(p) {
  const ri = p.riepilogo || {};
  return (ri.n_pagate || 0) + '/' + (ri.n_rate || p.n_rate);
}

// data prossima rata della riga (v1 r.7417): la data della prossima o '—'
export function prossimaData(p) {
  const ri = p.riepilogo || {};
  return ri.prossima ? formatData(ri.prossima.data_scadenza) : RZ_TEXTS.dash;
}

// residuo € della riga (v1 r.7416): fmtEur2(riepilogo.residuo)
export function residuoRiga(p, fmtEur2) {
  const ri = p.riepilogo || {};
  return fmtEur2(ri.residuo);
}

// ---- LETTURA dati (v1 r.7398): la lista dei piani ----
export async function readRateizzazioni(win) {
  const list = await apiPathFn(win, 'rateizzazioni.list').call(null);
  return list || [];
}

// ---- AZIONI e DELEGHE (stessi punti del v1) ----
// Editor "+ Nuovo piano" (modale v1). Il v3 passa il PROPRIO onSaved (refresh
// v3): il modale non chiama mai renderRateizzazioni diretto (niente interceptor).
export function apriNuovoPiano(win, onSaved) { win.AMM.editRateizzazione({}, onSaved); }
// Dettaglio del piano (modale v1 con rate editabili): si auto-gestisce il
// reload interno; nessun callback verso la lista (parità v1 r.7431).
export function apriDettaglio(win, id) { win.AMM.openRateizzazione(id); }
// Elimina (delSafe v1: alert d'errore se fallisce), poi onOk (toast + refresh v3).
export function eliminaPiano(win, id, onOk) {
  return win.AMM.delSafe(apiPathFn(win, 'rateizzazioni.remove').call(null, id), onOk);
}
